import "./tiptap.scss";

import { Content, Editor, EditorContent } from "@tiptap/react";
import { useEffect, useRef } from "react";

import { LinkMenu } from "./components/menus";

import { useBlockEditor } from "./hooks/useBlockEditor";

import { ContentItemMenu } from "./components/menus/ContentItemMenu";
import { SideMenuControls } from "./components/side-menu";
import { ColumnsMenu } from "./extensions/MultiColumn/menus";
import { TableAlignMenu, TableRowMenu, TableColumnMenu } from "./extensions/Table/menus";
import { CanvasControls } from "./components/canvas-controls";
import { CanvasToolbar } from "./components/canvas-toolbar";

const Tiptap = ({
  toolbarHeight,
  initialData,
  onChange,
  onBlur,
}: {
  toolbarHeight: number;
  initialData?: Content;
  onChange: (json: any) => void;
  onBlur: () => void;
}) => {
  const { editor } = useBlockEditor({
    onChange,
    onBlur,
  });

  useEffect(() => {
    if (!editor) return;
    const content: Content = initialData ?? [];

    if (content.length === 0 && !editor.isEmpty) {
      editor.commands.clearContent();
    } else if (content.length > 0) {
      try {
        editor.commands.setContent(content);
      } catch (error) {
        // Content check (enableContentCheck) throws on any node/mark not in the
        // schema. Don't let one bad section crash the whole editor — log it and
        // fall back to empty so the user can re-edit or delete the section.
        // eslint-disable-next-line no-console
        console.error("Failed to load section content; falling back to empty.", error);
        editor.commands.clearContent();
      }
    }
  }, [initialData, editor]);

  return (
    <div
      className="tiptap-wrapper flex-1 min-w-0 flex flex-row"
      style={{ height: `calc(100vh - ${toolbarHeight}px)` }}
    >
      <TipTapEditor editor={editor} />
      <SideMenuControls editor={editor} />
    </div>
  );
};

/**
 * Isolated instance of the editor to avoid re-rendering other components on changes
 * @param param0
 * @returns
 */
const TipTapEditor = ({ editor }: _TipTapEditorProps) => {
  const menuContainerRef = useRef(null);

  // Column wrapper: a fixed formatting toolbar pinned to the top, a scrolling
  // canvas in the middle, and the floating CanvasControls pinned bottom-right.
  return (
    <div className="flex-1 relative flex flex-col" style={{ minWidth: 0 }}>
      <CanvasToolbar editor={editor} />
      <div className="flex-1 pt-6 pb-6 pl-2 overflow-auto classy-scrollbar" ref={menuContainerRef}>
        <EditorContent editor={editor} />
        <ContentItemMenu editor={editor} />
        <LinkMenu editor={editor} appendTo={menuContainerRef} />
        <ColumnsMenu editor={editor} appendTo={menuContainerRef} />
        <TableRowMenu editor={editor} />
        <TableColumnMenu editor={editor} />
        <TableAlignMenu editor={editor} />
        {/*
        <ImageBlockMenu editor={editor} appendTo={menuContainerRef} />
        <HorizontaslLineBlockMenu editor={editor} appendTo={menuContainerRef} />
        <SpacerBlockMenu editor={editor} appendTo={menuContainerRef} />
        <PageBreakBlockMenu editor={editor} appendTo={menuContainerRef} /> */}
      </div>
      <CanvasControls />
    </div>
  );
};

interface _TipTapEditorProps {
  editor: Editor;
}

export default Tiptap;
